import AddressSuggestionsField from '@/components/ui/AddressSuggestionsField';
import OrganizationOfferSection from '../OrganizationOfferSection';
import OrganizationRegistrationField from '../OrganizationRegistrationField';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import { motion } from 'framer-motion';
import { OrganizationReviewStepProps } from './types';
import { PhoneInput } from '@/components/ui/phone-input';
import { Textarea } from '@/components/ui/textarea';

const OrganizationReviewStep = (props: OrganizationReviewStepProps) => {
  const {
    form,
    hasRegisteredOrganization = false,
    isIndividualEntrepreneur,
    isOfferAccepted,
    isPending,
    onAcceptedChange,
    onAddressTextChange,
    onBack,
    onBlurField,
    onChangeField,
    onClearAddressSelection,
    onSelectAddressSuggestion,
    onSubmit,
    submitAttempted,
    touched,
    validation
  } = props;

  const ogrnMaxLength = isIndividualEntrepreneur ? 15 : 13;

  return (
    <motion.form
      animate={{ opacity: 1, x: 0 }}
      className="space-y-8"
      exit={{ opacity: 0, x: 16 }}
      initial={{ opacity: 0, x: 16 }}
      key="review"
      noValidate
      onSubmit={onSubmit}
      transition={{ duration: 0.22, ease: 'easeOut' }}
    >
      <div className="px-4 py-3 text-xs text-muted-foreground rounded-2xl border border-border-strong bg-surface">
        Реквизиты подтянуты по ИНН&nbsp;
        <span className="font-semibold text-foreground">{form.inn}</span>. Проверьте и отредактируйте при необходимости
      </div>

      <div className="flex flex-col gap-4">
        <OrganizationRegistrationField
          disabled
          id="inn"
          inputMode="numeric"
          label="ИНН"
          onChange={(value) => onChangeField('inn', value.replace(/\D/g, '').slice(0, 12))}
          value={form.inn}
        />

        <OrganizationRegistrationField
          error={(touched.fullName || submitAttempted) ? validation.fullNameError : ''}
          id="fullName"
          isMultiline
          label="Полное наименование"
          onBlur={() => onBlurField('fullName')}
          onChange={(value) => onChangeField('fullName', value)}
          value={form.fullName}
        />

        <OrganizationRegistrationField
          error={(touched.fullNameEn || submitAttempted) ? validation.fullNameEnError : ''}
          helperText="Наименование латиницей для международных документов."
          id="fullNameEn"
          isMultiline
          label="Полное наименование EN"
          onBlur={() => onBlurField('fullNameEn')}
          onChange={(value) => onChangeField('fullNameEn', value)}
          value={form.fullNameEn}
        />

        <OrganizationRegistrationField
          error={(touched.shortName || submitAttempted) ? validation.shortNameError : ''}
          id="shortName"
          label="Сокращенное наименование"
          onBlur={() => onBlurField('shortName')}
          onChange={(value) => onChangeField('shortName', value)}
          value={form.shortName}
        />

        {isIndividualEntrepreneur ? (
          <OrganizationRegistrationField
            error={(touched.ogrn || submitAttempted) ? validation.ogrnError : ''}
            id="ogrn"
            inputMode="numeric"
            label="ОГРНИП"
            onBlur={() => onBlurField('ogrn')}
            onChange={(value) => onChangeField('ogrn', value.replace(/\D/g, '').slice(0, ogrnMaxLength))}
            value={form.ogrn}
          />
        ) : (
          <div className="grid grid-cols-2 gap-4">
            <OrganizationRegistrationField
              error={(touched.kpp || submitAttempted) ? validation.kppError : ''}
              id="kpp"
              inputMode="numeric"
              label="КПП"
              onBlur={() => onBlurField('kpp')}
              onChange={(value) => onChangeField('kpp', value.replace(/\D/g, '').slice(0, 9))}
              value={form.kpp}
            />
            <OrganizationRegistrationField
              error={(touched.ogrn || submitAttempted) ? validation.ogrnError : ''}
              id="ogrn"
              inputMode="numeric"
              label="ОГРН"
              onBlur={() => onBlurField('ogrn')}
              onChange={(value) => onChangeField('ogrn', value.replace(/\D/g, '').slice(0, ogrnMaxLength))}
              value={form.ogrn}
            />
          </div>
        )}

        {isIndividualEntrepreneur ? (
          <AddressSuggestionsField
            error={(touched.address || submitAttempted) ? validation.addressError : ''}
            id="address"
            label="Адрес места жительства (адрес регистрации ИП)"
            onBlur={() => onBlurField('address')}
            onClearSelection={onClearAddressSelection}
            onSelectSuggestion={(suggestion) => onSelectAddressSuggestion?.(suggestion)}
            onTextChange={(text) => onAddressTextChange?.(text)}
            placeholder="Начните вводить адрес"
            required
            scope="legal"
            value={form.address}
          />
        ) : (
          <div className="space-y-2">
            <Label
              className="text-sm font-semibold leading-5 text-muted-foreground"
              htmlFor="address"
            >
              Юридический адрес
            </Label>
            <Textarea
              className={cn(
                'min-h-[64px] px-4 py-3',
                'rounded-2xl bg-surface',
                (touched.address || submitAttempted) && validation.addressError && 'border-destructive ring-1 ring-destructive'
              )}
              disabled
              id="address"
              onBlur={() => onBlurField('address')}
              onChange={(event) => onChangeField('address', event.target.value)}
              rows={2}
              value={form.address}
            />
            {(touched.address || submitAttempted) && validation.addressError ? (
              <p className="px-3 text-xs text-[hsl(var(--error-text))]">{validation.addressError}</p>
            ) : null}
          </div>
        )}

        <OrganizationRegistrationField
          disabled
          error={(touched.addressEn || submitAttempted) ? validation.addressEnError : ''}
          helperText="Адрес латиницей для международных документов."
          id="addressEn"
          isMultiline
          label="Юридический адрес EN"
          onBlur={() => onBlurField('addressEn')}
          onChange={(value) => onChangeField('addressEn', value)}
          value={form.addressEn}
        />

        <div className="flex flex-col gap-2">
          <Label
            className="truncate text-sm font-semibold leading-5 text-muted-foreground"
            htmlFor="phone"
          >
            Телефон организации
          </Label>
          <PhoneInput
            className="border-border-strong"
            error={(touched.phone || submitAttempted) && Boolean(validation.phoneError)}
            id="phone"
            onBlur={() => onBlurField('phone')}
            onChange={(phone) => onChangeField('phone', phone)}
            required
            value={form.phone}
          />
          {(touched.phone || submitAttempted) && validation.phoneError ? (
            <p className="px-3 text-xs text-[hsl(var(--error-text))]">{validation.phoneError}</p>
          ) : null}
        </div>
      </div>

      <OrganizationOfferSection
        isAccepted={isOfferAccepted}
        onAcceptedChange={onAcceptedChange}
        submitAttempted={submitAttempted}
      />

      <div className="flex flex-col gap-3 pt-1">
        <Button
          className="w-full"
          loading={isPending}
          size="md"
          type="submit"
        >
          {hasRegisteredOrganization
            ? isPending ? 'Обновляем доступ...' : 'Обновить доступ'
            : isPending ? 'Сохраняем...' : 'Зарегистрировать организацию'}
        </Button>
        <Button
          className="w-full"
          disabled={isPending || hasRegisteredOrganization}
          leftIcon={<ArrowLeft className="size-5" />}
          onClick={onBack}
          size="md"
          type="button"
          variant="secondary"
        >
          Назад
        </Button>
      </div>
    </motion.form>
  );
};

export default OrganizationReviewStep;